import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { allArticles, articleMap } from '@/mocks/articles';

export default function ArticlePage() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://pandabuy.beauty';
  const pathSlug = location.pathname.replace(/^\//, '').replace(/\/$/, '');
  const article = articleMap[pathSlug] || allArticles[0];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700">Back to Home</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const articleUrl = `${siteUrl}/${article.slug}`;

  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.subtitle,
    "url": articleUrl,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": articleUrl
    },
    "image": article.image,
    "author": {
      "@type": "Organization",
      "name": "Shopping Research Team"
    },
    "datePublished": "2026-06-20",
    "dateModified": "2026-06-29",
    "publisher": {
      "@type": "Organization",
      "name": "Pandabuy Beauty",
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg"
      }
    }
  };

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": siteUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": article.title,
        "item": articleUrl
      }
    ]
  };

  const faqStructuredData = article.faqs && article.faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": article.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleStructuredData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }} />
      {faqStructuredData && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }} />
      )}
      <Navbar />

      <main>
        <section className="relative w-full pt-24 pb-12 md:pt-28 md:pb-16 bg-background-100">
          <div className="w-full px-6 md:px-10 lg:px-16">
            <div className="max-w-3xl mx-auto">
              <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                {article.category && (
                  <>
                    <span className="text-foreground-400">{article.category}</span>
                    <i className="ri-arrow-right-s-line text-xs"></i>
                  </>
                )}
                <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
              </nav>
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4 whitespace-nowrap">
                Updated {article.dateUpdated}
              </span>
              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                {article.subtitle}
              </p>
              <div className="flex items-center gap-3 mt-6 text-xs text-foreground-500">
                <span>Written by Shopping Research Team</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span>{article.readTime}</span>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-8 md:py-10">
          <div className="w-full px-6 md:px-10 lg:px-16">
            <div className="max-w-3xl mx-auto">
              <div className="bg-background-100 rounded-xl p-5 md:p-6">
                <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-3">Table of Contents</h2>
                <ul className="space-y-2 text-sm">
                  {article.sections.filter(s => s.heading !== 'Frequently Asked Questions').map((section) => (
                    <li key={section.id}>
                      <a href={`#${section.id}`} className="text-foreground-600 hover:text-primary-600 transition-colors">
                        {section.heading}
                      </a>
                    </li>
                  ))}
                  {article.faqs.length > 0 && (
                    <li>
                      <a href="#frequently-asked-questions" className="text-foreground-600 hover:text-primary-600 transition-colors">
                        Frequently Asked Questions
                      </a>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <article className="bg-background-50 pb-16 md:pb-20">
          <div className="w-full px-6 md:px-10 lg:px-16">
            <div className="max-w-3xl mx-auto">
              <div className="prose-custom space-y-8 text-foreground-700 leading-relaxed">
                {article.sections.map((section) => (
                  <section key={section.id} id={section.id}>
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                      {section.heading}
                    </h2>
                    {section.paragraphs.map((para, i) => (
                      <p key={i} className="text-sm md:text-base mb-4" dangerouslySetInnerHTML={{ __html: para }}></p>
                    ))}
                    {section.image && (
                      <div className="my-6 rounded-xl overflow-hidden">
                        <img
                          src={section.image}
                          alt={section.heading}
                          className="w-full h-auto object-cover rounded-xl"
                          loading="lazy"
                        />
                      </div>
                    )}
                  </section>
                ))}

                {article.faqs.length > 0 && (
                  <section id="frequently-asked-questions">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                      Frequently Asked Questions
                    </h2>
                    <div className="space-y-5">
                      {article.faqs.map((faq, i) => (
                        <div key={i}>
                          <h3 className="font-semibold text-foreground-900 mb-2">{faq.question}</h3>
                          <p className="text-sm md:text-base text-foreground-600">{faq.answer}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                <div className="mt-10 pt-8 border-t border-background-300">
                  <p className="text-sm text-foreground-600 mb-4 text-center">
                    Ready to start shopping? Visit our main site for the complete Pandabuy Spreadsheet collection organized across all fourteen categories.
                  </p>
                  <div className="flex flex-wrap justify-center gap-3">
                    <Link
                      to="/"
                      className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      Back to Home
                    </Link>
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="px-5 py-2.5 rounded-full bg-background-200 text-foreground-800 text-sm font-medium whitespace-nowrap hover:bg-background-300 transition-colors cursor-pointer"
                    >
                      Pandabuy Sheet
                      <i className="ri-arrow-right-up-line ml-1.5 text-xs"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>

        <section className="bg-background-100 py-12 md:py-16">
          <div className="w-full px-6 md:px-10 lg:px-16">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">
                Browse Pandabuy Categories
              </h2>
              <div className="flex flex-wrap justify-center gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-4 py-2 rounded-full bg-background-200/70 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-300/70 transition-colors cursor-pointer"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-12 md:py-16">
          <div className="w-full px-6 md:px-10 lg:px-16">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">
                More Pandabuy Spreadsheet Guides
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {allArticles.filter(a => a.slug !== article.slug).slice(0, 6).map((related) => (
                  <Link
                    key={related.slug}
                    to={`/${related.slug}`}
                    className="flex gap-3 p-4 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer group"
                  >
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
                        {related.title}
                      </h3>
                      <span className="text-xs text-foreground-500">{related.readTime}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}